import InformationList from '@/src/components/common/InformationList'
import useProductInventory from '@/src/hooks/useProductInventory'
import { ProductInventoryResMock } from '@/src/types/inventory.type'
import { capitalizeDrugName, concatenateStrengthsConcise, formatCurrency } from '@/src/utils'
import { Loader, SideDrawer } from '@ui'
import { Box } from 'grommet'
import { useState } from 'react'

type Props = {
  onClose: () => void
  id?: string
}

const options = ['Batch Details', 'Regulatory', 'Stock & Pricing']

export const InventoryDetails = ({ onClose, id }: Props) => {
  const { singleInventory, inventoriesIsLoading } = useProductInventory({ id: id })
  const [tab, setTab] = useState('Batch Details')

  const organizeBatchDetails = (productInventory: ProductInventoryResMock) => {
    const attributes = productInventory?.attributes || {}
    const formatValue = (value: any) => {
      if (value === null || value === undefined || value === '') return 'N/A'
      return value
    }

    const coreDetails = {
      'Drug Name': formatValue(capitalizeDrugName(attributes?.drug_name)),
      Strength: formatValue(attributes?.strength || concatenateStrengthsConcise(attributes?.api_strengths)),
      'Batch Number': formatValue(attributes?.batch_number),
      'Expiry Date': formatValue(attributes?.expiry_date),
      'Date of Manufacture': formatValue(attributes?.date_of_manufacture),
      'Current Status': formatValue(attributes?.status),
      'Stock Location': formatValue(attributes?.stock_location)
    }

    const stockAndPricing = {
      'Current Stock': formatValue(attributes?.stock_quantity),
      'Quantity Received': formatValue(attributes?.quantity_received),
      'Selling Price': formatValue(formatCurrency(Number(attributes?.selling_price))),
      'Unit Cost': formatValue(formatCurrency(Number(attributes?.unit_cost))),
      'Pack Content': formatValue(`${attributes?.pack_quantity || 'N/A'} ${attributes?.pack_content_unit || ''}`),
      Packaging: formatValue(attributes?.packaging),
      'Inventory Unit': formatValue(attributes?.inventory_unit)
    }

    const regulatoryAndSystemInfo = {
      'NAFDAC Number': formatValue(attributes?.nafdac_number)
    }

    return {
      coreDetails,
      stockAndPricing,
      regulatoryAndSystemInfo
    }
  }

  const batchDetails = organizeBatchDetails(singleInventory as ProductInventoryResMock)

  const tabDataMap = {
    'Batch Details': batchDetails.coreDetails,
    Regulatory: batchDetails.regulatoryAndSystemInfo,
    'Stock & Pricing': batchDetails.stockAndPricing
  }

  return (
    <SideDrawer
      onClose={onClose}
      options={options}
      setTab={setTab}
      style={{ padding: '16px 28px' }}
      title="Batch Details"
    >
      {inventoriesIsLoading ? (
        <Loader />
      ) : (
        <Box flex style={{ overflow: 'hidden', position: 'relative' }}>
          {options.map((option, index) => {
            const data = tabDataMap[tab as keyof typeof tabDataMap]

            const isActive = tab === option
            const transform = `translateX(${isActive ? '0' : index > options.indexOf(tab) ? '100%' : '-100%'})`

            return (
              <div
                className={`step-slide ${isActive ? 'active' : ''}`}
                key={option}
                style={{
                  position: 'absolute',
                  transform,
                  transition: 'all 0.3s ease-in-out',
                  width: '100%',
                  padding: '0 20px'
                }}
              >
                <InformationList data={data || {}} />
              </div>
            )
          })}
        </Box>
      )}
    </SideDrawer>
  )
}
